import { useEffect, useState } from "react";
import { Button, ButtonGroup, ButtonToolbar, Col, Container, Form, Modal, Row } from "react-bootstrap";
import { SXEditField } from "@common/components/ui-components/SXEditField";
import { SXDropdown, type NGDropdownListItem } from "@common/components/ui-components/SXDropdown";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { AppType } from "@common/types/sharedTypes";
import { appTypeRequiresMaterial, getAppTypeLabel } from "../../helpers/appTypes";
import { ng } from "@common/ngds";
import KeywordPills from "../ui-components/SXKeywordPill";
import { setCreateNew } from "../../features/ui/uiSlice";
import { resetFile, updateFile } from "../../features/user/userSlice";
import { store } from "../../app/store";
import type { ProjectFile } from "../../ProjectExplorer";
import type { RightPanelProps } from "./panelProps";

export default function RightPanel(props: RightPanelProps) {
  const {
    user,
    selectedProjectName,
    checkedCount,
    sfile,
    selectedFile,
    isCreatingNew,
    t,
    dispatch,
    parseAppType,
    setShowSameNameWarning,
    showSameNameWarning,
    keywordPillsRef,
    setKeywordInputDirty,
    configs_ui,
    setPendingMultiDelete,
    setShowMultiDeleteModal,
    showMultiDeleteModal,
    pendingMultiDelete,
    deleteMultipleProjects,
    setSelectedFileIds,
    getUser,
    selectedFileAppType,
    copyOFCurrentSelectedFile,
    access,
    showSaveButton,
    updateFileMeta,
    refreshSelectedProject,
    setCopyOfCurrentSelectedFile,
    setProjectName,
    setPendingDelete,
    setShowDeleteModal,
    showDeleteModal,
    pendingDelete,
    nextFileListName,
    deleteProject,
    fileTypeRef,
    mtrlTemplate,
    setMtrlTemplate,
    projectName,
    createProject,
    parseUTC,
  } = props;

  const typeOptions: NGDropdownListItem<AppType>[] = [
    ...(access["beam-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_BEAM, name: "Beam" }] : []),
    ...(access["foundation-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_FOUNDATION, name: "Foundation" }] : []),
    ...(access["column-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_COLUMN, name: "Column" }] : []),
    ...(configs_ui.IS_DEV || access["frame-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_FRAME, name: "Frame" }] : []),
    ...(configs_ui.IS_DEV || access["section-app"] == 200
      ? [{ id: AppType.SIMPLEX_APP_SECTION, name: "Section" }]
      : []),
    ...(configs_ui.IS_DEV || access["retainingwall-app"] == 200
      ? [{ id: AppType.SIMPLEX_APP_RETAININGWALL, name: "Retaining Wall" }]
      : []),
    ...(configs_ui.IS_DEV || access["basementwall-app"] == 200
      ? [{ id: AppType.SIMPLEX_APP_BASEMENT_WALL, name: "Basement Wall" }]
      : []),
    ...(configs_ui.IS_DEV || access["pile-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_PILE, name: "Pile" }] : []),
    ...(configs_ui.IS_DEV || access["cpt-app"] == 200 ? [{ id: AppType.SIMPLEX_APP_CPT, name: "CPT" }] : []),
  ];

  const materialOptions: NGDropdownListItem<ng.material.Mtrl>[] = [
    { id: ng.material.Mtrl.MTRL_CONCRETE, name: t("materials.concrete") },
    { id: ng.material.Mtrl.MTRL_STEEL, name: t("materials.steel") },
    { id: ng.material.Mtrl.MTRL_TIMBER, name: t("materials.timber") },
  ];

  // Beam and Column are the only app types where the user picks a material for a new file.
  const needsMaterial = appTypeRequiresMaterial(isCreatingNew);
  // Column only: create the file with its example loads. Not persisted as a user setting.
  const [addDefaultLoads, setAddDefaultLoads] = useState(false);
  useEffect(() => {
    setAddDefaultLoads(false);
  }, [isCreatingNew]);
  const currentProjectFiles = user.projectdigest[selectedProjectName]?.files ?? [];
  const normalizedProjectName = projectName.trim();
  const hasDuplicateCreateFileName =
    Boolean(isCreatingNew) &&
    normalizedProjectName.length > 0 &&
    currentProjectFiles.some((file) => file.name === normalizedProjectName);
  const canCreateFile =
    Boolean(isCreatingNew) &&
    normalizedProjectName.length > 0 &&
    !hasDuplicateCreateFileName &&
    (!needsMaterial || (mtrlTemplate !== undefined && mtrlTemplate > -1));

  return (
    <Col md={3} className="pt-2 pb-3 h-100">
      <div
        id="right-panel"
        style={{
          overflow: "hidden",
          height: "100%",
          ...(user.projectdigest[selectedProjectName]?.status === "inactive"
            ? { backgroundColor: "#eee", opacity: 0.6, pointerEvents: "none" }
            : {}),
        }}
      >
        <Container id="project-file-info-container" fluid className="project-file-info-layout">
          <Row className="header-bg-list project-file-info-header-row">
            <Col style={{ height: 30 }} className="ps-0">
              <span className="header-title">{t("project_file_data")}</span>
            </Col>
          </Row>
          <Row className="project-file-info-scroll-row project-file-info-content-row ">
            <Col xs={12} className="project-file-info-body project-file-info-body-padding">
              {sfile ? (
                <Container
                  id="file-data-inputs"
                  key={selectedFile}
                  fluid
                  className="project-file-info-card-container sx-table-container"
                >
                  {!checkedCount && (
                    <Row className="card-content file-info-box right-panel-surface-card editfield-container pb-4">
                      <Row className="container-row ">
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.name")}</div>
                          <div className="ps-0" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-name"
                              fontSize={12}
                              type="text"
                              instantupdate
                              disabled={checkedCount > 0}
                              val={sfile.name ?? ""}
                              placeholder="project name"
                              onUpdate={(newValue) => {
                                const val = String(newValue);
                                dispatch(
                                  updateFile({
                                    file: sfile,
                                    field: "name",
                                    index: selectedFile,
                                    value: val,
                                    selectedProject: selectedProjectName,
                                  }),
                                );
                                setShowSameNameWarning(false);
                                if (
                                  user &&
                                  user.projectdigest[selectedProjectName].files.findIndex(
                                    (p: ProjectFile) => p.name === val && p.id !== sfile.id,
                                  ) !== -1
                                ) {
                                  setShowSameNameWarning(true);
                                }
                              }}
                            />
                            {showSameNameWarning && (
                              <div className="text-danger" style={{ fontSize: 12 }}>
                                {t("name_already_exists")}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row app-type-row ">
                        <Col className="meta-field-stack">
                          <div className="editfield-label pb-1">{t("meta_data.type")}</div>
                          <div className="ps-0  app-type-value">
                            {(() => {
                              const appType = parseAppType(sfile.type);
                              const color = checkedCount > 0 ? "gray" : undefined;
                              const label = appType === null ? "Unknown" : getAppTypeLabel(appType);
                              if (label === "Unknown") {
                                return (
                                  <span className="badge bg-info me-2" style={{ color }}>
                                    UNKNOWN
                                  </span>
                                );
                              }
                              return (
                                <span className="badge bg-secondary me-2" style={{ color }}>
                                  {label.toUpperCase()}
                                </span>
                              );
                            })()}
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row pt-0">
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.description")}</div>
                          <div className="ps-0" style={{ minHeight: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-description"
                              fontSize={12}
                              type="text"
                              isTextArea
                              instantupdate
                              disabled={checkedCount > 0}
                              val={sfile.description ?? ""}
                              placeholder={t("meta_data.description")}
                              onUpdate={(newValue) => {
                                dispatch(
                                  updateFile({
                                    file: sfile,
                                    field: "description",
                                    value: String(newValue),
                                    selectedProject: selectedProjectName,
                                    index: selectedFile,
                                  }),
                                );
                              }}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row">
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.comments")}</div>
                          <div className="ps-0" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-comments"
                              fontSize={12}
                              type="text"
                              instantupdate
                              disabled={checkedCount > 0}
                              val={sfile.comments ?? ""}
                              placeholder={t("meta_data.comments")}
                              onUpdate={(newValue) => {
                                dispatch(
                                  updateFile({
                                    file: sfile,
                                    field: "comments",
                                    value: String(newValue),
                                    selectedProject: selectedProjectName,
                                    index: selectedFile,
                                  }),
                                );
                              }}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row id="keyword-pill-row" className="container-row">
                        <Col className="meta-field-stack">
                          <div className="editfield-label " style={{ color: checkedCount > 0 ? "gray" : "black" }}>
                            {t("tags")}
                          </div>
                          <div className="ps-0" style={{ minHeight: 26 }}>
                            <KeywordPills
                              ref={keywordPillsRef}
                              sfile={sfile}
                              selectedProjectName={selectedProjectName}
                              selectedFile={selectedFile}
                              checkedCount={checkedCount}
                              onDirtyChange={setKeywordInputDirty}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row  two-col-metadata-row">
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.created")}</div>
                          <div className="ps-0 me-2" style={{ height: 26 }}>
                            <SXEditField
                              disabled
                              fullWidth
                              textAlign="left"
                              id="file-created"
                              fontSize={12}
                              type="text"
                              val={sfile.created ? parseUTC(sfile.created).toLocaleString() : ""}
                            />
                          </div>
                        </Col>
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.last_modified")}</div>
                          <div className="ps-0" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-last-modified"
                              fontSize={12}
                              type="text"
                              disabled
                              val={sfile.lastModified ? parseUTC(sfile.lastModified).toLocaleString() : ""}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row pt-0 two-col-metadata-row">
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.modified_by")}</div>
                          <div className="ps-0 me-2" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-modified-by"
                              fontSize={12}
                              type="text"
                              disabled
                              val={sfile.modifiedBy ?? ""}
                            />
                          </div>
                        </Col>
                        <Col className="meta-field-stack">
                          <div className="editfield-label">{t("meta_data.author")}</div>
                          <div className="ps-0 px-0" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-author"
                              fontSize={12}
                              type="text"
                              disabled
                              val={sfile.author ?? ""}
                            />
                          </div>
                        </Col>
                      </Row>

                      <Row className="container-row pt-0 two-col-metadata-row">
                        <Col className="meta-field-stack" xs={6}>
                          <div className="editfield-label">{t("meta_data.signature")}</div>
                          <div className="ps-0 me-2" style={{ height: 26 }}>
                            <SXEditField
                              fullWidth
                              textAlign="left"
                              id="file-signature"
                              fontSize={12}
                              type="text"
                              disabled
                              val={sfile.signature ?? ""}
                            />
                          </div>
                        </Col>
                      </Row>
                    </Row>
                  )}

                  <Modal
                    className="sxmodal"
                    show={showMultiDeleteModal}
                    onHide={() => setShowMultiDeleteModal(false)}
                    centered
                  >
                    <Modal.Header closeButton>
                      <Modal.Title>{t("delete_modal.confirm_delete_title") || "Confirm Deletion"}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      {pendingMultiDelete.length > 0 ? (
                        <>
                          {t("delete_modal.confirm_delete_message")}
                          <ul style={{ marginTop: 10 }}>
                            {pendingMultiDelete.map((file, idx: number) => (
                              <li key={file.id + "-" + idx}>
                                <b>{file.name || file.id}</b>
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <>{t("no_files_selected")}</>
                      )}
                    </Modal.Body>
                    <Modal.Footer>
                      <Button className="secondary-btn" onClick={() => setShowMultiDeleteModal(false)}>
                        {t("buttons.cancel") || "Cancel"}
                      </Button>
                      <Button
                        className="secondary-btn"
                        onClick={async () => {
                          if (pendingMultiDelete.length > 0) {
                            await deleteMultipleProjects(
                              pendingMultiDelete.map((f) => ({ id: f.id, type: f.type, guid: f.guid })),
                            );
                            await getUser();
                          }
                          setSelectedFileIds([]);
                          setShowMultiDeleteModal(false);
                          setPendingMultiDelete([]);
                        }}
                      >
                        {t("buttons.delete") || "Delete"}
                      </Button>
                    </Modal.Footer>
                  </Modal>

                  {checkedCount === 0 && (
                    <Row className="card-actions project-file-toolbar ps-0">
                      <Col xs={12}>
                        <ButtonToolbar className="" aria-label="actions">
                          <ButtonGroup className="me-2" aria-label="First group">
                            <Button
                              className="secondary-btn"
                              disabled={!showSaveButton}
                              onClick={() => {
                                if (!copyOFCurrentSelectedFile) return;

                                dispatch(
                                  resetFile({
                                    file: copyOFCurrentSelectedFile,
                                    selectedProjectName,
                                    index: selectedFile,
                                  }),
                                );
                                setKeywordInputDirty(false);
                                setShowSameNameWarning(false);
                              }}
                            >
                              {t("buttons.cancel")}
                            </Button>
                            <Button
                              className="primary-btn"
                              disabled={!showSaveButton}
                              onClick={async () => {
                                keywordPillsRef.current?.commitPending();
                                const stateBeforeSave =
                                  store.getState().UserSlice?.user?.projectdigest?.[selectedProjectName]?.files?.[
                                    selectedFile
                                  ];
                                const fileToSave = stateBeforeSave || sfile;
                                await updateFileMeta(fileToSave.id, fileToSave);
                                await getUser();
                                const freshFiles =
                                  store.getState().UserSlice?.user?.projectdigest?.[selectedProjectName]?.files;
                                const freshFile = freshFiles?.[selectedFile];
                                setCopyOfCurrentSelectedFile(freshFile ? { ...freshFile } : { ...fileToSave });
                                setKeywordInputDirty(false);
                                refreshSelectedProject();
                              }}
                            >
                              {t("buttons.save")}
                            </Button>
                          </ButtonGroup>
                          <ButtonGroup className="sx-button-group-squares" aria-label="Second group"></ButtonGroup>
                          <ButtonGroup aria-label="Third group" className="sx-button-group-squares ">
                            {/* <Button
                              className="secondary-btn"
                              style={{ textAlign: "right" }}
                              title={t("buttons.delete")}
                              onClick={() => {
                                setPendingDelete({
                                  id: sfile.id,
                                  guid: sfile.guid as string,
                                  type: selectedFileAppType,
                                });
                                setShowDeleteModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button> */}
                            <Modal
                              className="sxmodal"
                              show={showDeleteModal}
                              onHide={() => setShowDeleteModal(false)}
                              centered
                            >
                              <Modal.Header closeButton>
                                <Modal.Title>
                                  {t("delete_modal.confirm_delete_title") || "Confirm Deletion"}
                                </Modal.Title>
                              </Modal.Header>
                              <Modal.Body>
                                {pendingDelete && sfile && pendingDelete.id === sfile.id && (
                                  <>
                                    {t("delete_modal.confirm_delete_message")} <b>{sfile.name}</b> ?
                                  </>
                                )}
                              </Modal.Body>
                              <Modal.Footer>
                                <Button className="secondary-btn" onClick={() => setShowDeleteModal(false)}>
                                  {t("buttons.cancel") || "Cancel"}
                                </Button>
                                <Button
                                  className="secondary-btn"
                                  onClick={async () => {
                                    setProjectName(nextFileListName);
                                    if (pendingDelete) {
                                      await deleteProject(pendingDelete.id, pendingDelete.guid, pendingDelete.type);
                                      await getUser();
                                    }
                                    setShowDeleteModal(false);
                                    setPendingDelete(null);
                                  }}
                                >
                                  {t("buttons.delete") || "Delete"}
                                </Button>
                              </Modal.Footer>
                            </Modal>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Row>
                  )}
                </Container>
              ) : !isCreatingNew ? (
                <Col className="p-3 text-left">
                  <h5>{t("no_file_selected")}</h5>
                  <p>{t("select_a_file_to_view_its_data")}</p>
                </Col>
              ) : (
                ""
              )}

              {isCreatingNew && (
                <>
                  <Container
                    fluid
                    className="editfield-container project-file-info-create-container sx-table-container"
                  >
                    <Row className="card-content file-info-box right-panel-surface-card">
                      <Row className="justify-items-center align-items-center" style={{ height: 30 }}>
                        <Col xs="4">
                          <div className="editfield-label">{t("meta_data.type")}</div>
                        </Col>
                        <Col>
                          <SXDropdown
                            id="create-file-type-select"
                            name="create-file-type"
                            className="editfield-border"
                            style={{ fontSize: 12 }}
                            list={typeOptions}
                            val={typeOptions.find((option) => option.id === isCreatingNew) ?? null}
                            lang=""
                            rawNames
                            localisation={false}
                            notSelectedName="select_type"
                            onUpdate={(value) => {
                              if (!value) {
                                return;
                              }
                              const type = value.id;
                              dispatch(setCreateNew(type));
                              if (type == AppType.SIMPLEX_APP_FOUNDATION) {
                                setMtrlTemplate(ng.material.Mtrl.MTRL_CONCRETE);
                              }
                              if (type == AppType.SIMPLEX_APP_FRAME) {
                                setMtrlTemplate(ng.material.Mtrl.MTRL_CONCRETE);
                              }
                              if (type == AppType.SIMPLEX_APP_SECTION) {
                                setMtrlTemplate(ng.material.Mtrl.MTRL_CONCRETE);
                              }
                              if (
                                type == AppType.SIMPLEX_APP_RETAININGWALL ||
                                type == AppType.SIMPLEX_APP_BASEMENT_WALL ||
                                type == AppType.SIMPLEX_APP_PILE ||
                                type == AppType.SIMPLEX_APP_CPT
                              ) {
                                // These app types have no material.
                                setMtrlTemplate(undefined);
                              }
                            }}
                          />
                        </Col>
                      </Row>
                      {needsMaterial && (
                        <Row className="justify-items-center align-items-center mt-2" style={{ height: 30 }}>
                          <Col xs="4">
                            <div className="editfield-label">{t("material")}</div>
                          </Col>
                          <Col>
                            <SXDropdown
                              id="create-file-material-select"
                              name="create-file-material"
                              className="editfield-border"
                              list={materialOptions}
                              val={materialOptions.find((option) => option.id === mtrlTemplate) ?? null}
                              lang=""
                              rawNames
                              localisation={false}
                              notSelectedName="select_material"
                              onUpdate={(value) => {
                                setMtrlTemplate(value ? value.id : undefined);
                              }}
                            />
                          </Col>
                        </Row>
                      )}

                      {isCreatingNew == AppType.SIMPLEX_APP_COLUMN && (
                        <Row className="justify-items-center align-items-center mt-2" style={{ height: 30 }}>
                          <Col xs="4">
                            <div className="editfield-label">{t("add_default_loads")}</div>
                          </Col>
                          <Col>
                            <Form.Check
                              id="create-file-add-default-loads"
                              type="checkbox"
                              checked={addDefaultLoads}
                              onChange={(e) => setAddDefaultLoads(e.target.checked)}
                            />
                          </Col>
                        </Row>
                      )}

                      <Row className="justify-items-center align-items-center mt-2" style={{ height: 30 }}>
                        <Col xs="4">
                          <div className="editfield-label">{t("meta_data.name")}</div>
                        </Col>
                        <Col>
                          <SXEditField
                            fullWidth
                            textAlign="left"
                            id="create-file-name"
                            fontSize={12}
                            type="text"
                            instantupdate
                            border
                            val={projectName}
                            placeholder={t("file_name")}
                            onUpdate={(newValue) => {
                              setProjectName(String(newValue));
                            }}
                          />

                          {hasDuplicateCreateFileName && (
                            <div className="text-danger" style={{ fontSize: 12 }}>
                              {t("name_already_exists")}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Row>
                    <Row className="card-actions project-file-toolbar ps-0">
                      <Col xs={12}>
                        <ButtonToolbar className="" aria-label="actions">
                          <ButtonGroup className="me-2" aria-label="First group">
                            <Button
                              className="secondary-btn"
                              onClick={() => {
                                dispatch(setCreateNew(null));
                                setProjectName("");
                              }}
                            >
                              {t("buttons.cancel")}
                            </Button>

                            <Button
                              className="primary-btn"
                              disabled={!canCreateFile}
                              onClick={async () => {
                                if (!canCreateFile) return;

                                await createProject(
                                  normalizedProjectName,
                                  isCreatingNew as AppType,
                                  mtrlTemplate ?? ng.material.Mtrl.MTRL_UNSPECIFIED,
                                  {
                                    guid: user.projectdigest[selectedProjectName].guid,
                                    name: user.projectdigest[selectedProjectName].name,
                                  },
                                  isCreatingNew == AppType.SIMPLEX_APP_COLUMN && addDefaultLoads,
                                );
                                const files = user.projectdigest[selectedProjectName]?.files;
                                if (files && files.length > 0) {
                                  const newIndex = files.findIndex(
                                    (f: ProjectFile) => f.name === normalizedProjectName,
                                  );
                                  if (newIndex !== -1) {
                                    props.setSelectedFile(newIndex);
                                  } else {
                                    props.setSelectedFile(files.length - 1);
                                  }
                                  dispatch(setCreateNew(null));
                                } else {
                                  props.setSelectedFile(0);
                                  dispatch(setCreateNew(null));
                                }
                              }}
                            >
                              {t("buttons.create")}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Row>
                  </Container>
                </>
              )}
            </Col>
          </Row>
        </Container>
      </div>
    </Col>
  );
}
